import { useEffect } from 'react'
import { Sparkles, Star } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import confetti from 'canvas-confetti'
import { isGamificationEnabled } from '@tfa/shared'
import { useAuth } from '../../hooks/useAuth'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  icon: LucideIcon
  title: string
  subtitle?: string
  /** Total XP earned to display in the chip. Set to 0 to hide. */
  xp?: number
  /** Пояснение под XP-чипом (напр. «За прошлые дни прогресс не начисляется»). */
  note?: string
  /** Linear-gradient string for icon bubble. Defaults to a violet gradient. */
  gradient?: string
  /** Confetti palette. */
  confettiColors?: string[]
  /** Button text. Default: «Продолжить». */
  ctaLabel?: string
  /** CTA-обработчик. По умолчанию закрывает модалку (onClose). */
  onCtaClick?: () => void
  /** test-id для автотестов; по умолчанию modal-reward-congrats. */
  testId?: string
}

const SPARKS = [
  { top: -14, left: 18, rotate: -20, delay: 0 },
  { top: -10, right: 16, rotate: 15, delay: 0.08 },
  { top: 30, left: -14, rotate: -35, delay: 0.14 },
  { top: 28, right: -12, rotate: 30, delay: 0.2 },
]

const DEFAULT_COLORS = ['#6C5CE7', '#A29BFE', '#FFD60A', '#34C759', '#FF9500']

export function RewardCongrats({
  open,
  onClose,
  icon: Icon,
  title,
  subtitle,
  xp = 0,
  note,
  gradient = 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)',
  confettiColors = DEFAULT_COLORS,
  ctaLabel = 'Продолжить',
  onCtaClick,
  testId = 'modal-reward-congrats',
}: Props) {
  const { user } = useAuth()
  const rewardsOn = isGamificationEnabled((user as { mode?: string | null })?.mode ?? null, (user as { completedAt?: string | null })?.completedAt ?? null)

  useEffect(() => {
    if (!open) return
    const end = Date.now() + 1800
    const frame = () => {
      confetti({ particleCount: 5, angle: 60, spread: 55, origin: { x: 0, y: 0.65 }, colors: confettiColors })
      confetti({ particleCount: 5, angle: 120, spread: 55, origin: { x: 1, y: 0.65 }, colors: confettiColors })
      if (Date.now() < end) requestAnimationFrame(frame)
    }
    frame()
  }, [open, confettiColors])

  // Авто-закрытие — чтобы пользователю не приходилось вручную сворачивать
  // попап после успешной сдачи (анимация играет ~1.8с + 0.7с послевкусие).
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => { onClose() }, 2500)
    return () => clearTimeout(t)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 400,
            background: 'rgba(10,18,40,0.62)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, fontFamily: F,
          }}
        >
          <motion.div
            data-testid={testId}
            initial={{ scale: 0.82, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.88, opacity: 0, y: 12 }}
            transition={{ type: 'spring', damping: 20, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 340,
              borderRadius: 28, padding: '32px 24px 28px',
              background: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(32px)',
              WebkitBackdropFilter: 'blur(32px)',
              boxShadow: '0 28px 72px rgba(108,92,231,0.28), 0 0 0 1px rgba(255,255,255,0.6)',
              textAlign: 'center', position: 'relative', overflow: 'visible',
            }}
          >
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: 4 }}>
              <div
                style={{
                  width: 120, height: 120, borderRadius: '50%',
                  background: gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 12px 32px rgba(108,92,231,0.35)',
                }}
              >
                <Icon size={56} color="white" strokeWidth={2.2} />
              </div>
              {SPARKS.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: s.delay, type: 'spring', stiffness: 400 }}
                  style={{
                    position: 'absolute',
                    top: s.top,
                    left: (s as { left?: number }).left,
                    right: (s as { right?: number }).right,
                    transform: `rotate(${s.rotate}deg)`,
                  }}
                >
                  <Sparkles size={18} color="#FFD60A" fill="#FFD60A" />
                </motion.div>
              ))}
            </div>

            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C2D41', margin: '12px 0 6px', lineHeight: 1.2 }}>
              {title}
            </h1>
            {subtitle && (
              <p style={{ fontSize: 14, color: '#6B7B8D', margin: '0 0 20px', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                {subtitle}
              </p>
            )}

            {rewardsOn && xp > 0 && (
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.18, type: 'spring', stiffness: 400 }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  background: 'linear-gradient(135deg,#FFD60A,#FF9500)',
                  borderRadius: 22, padding: '9px 20px', marginBottom: 24,
                  boxShadow: '0 6px 18px rgba(255,149,0,0.35)',
                }}
              >
                <Star size={16} color="white" fill="white" />
                <span style={{ fontSize: 16, fontWeight: 800, color: 'white' }}>+{xp} XP</span>
              </motion.div>
            )}

            {note && (
              <p style={{ fontSize: 13, color: '#9AA5B4', margin: '0 0 20px', lineHeight: 1.4 }}>
                {note}
              </p>
            )}

            <button
              data-testid="btn-close-reward-congrats"
              onClick={onCtaClick ?? onClose}
              style={{
                display: 'block', width: '100%', height: 52,
                borderRadius: 16, border: 'none',
                background: gradient,
                color: 'white', fontSize: 16, fontWeight: 700,
                cursor: 'pointer', fontFamily: F,
                boxShadow: '0 8px 24px rgba(108,92,231,0.32)',
              }}
            >
              {ctaLabel}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
